import { useRef, useState } from 'react';
import type { PosGiftVariantOption } from '../types';

export interface GiftVariantPickerRequest {
  productName: string;
  requiredQty: number;
  options: PosGiftVariantOption[];
}

/**
 * Cầu nối Promise ↔ dialog chọn biến thể quà tặng (`GiftVariantPickerDialog`)
 * khi voucher/CTKM `free_gift` trỏ tới một product có nhiều variant.
 *
 * Tách ra từ `VoucherModal` (nơi hàm `pickGiftVariant` này từng là hàm cục
 * bộ) để `POS/index.tsx` dùng LẠI được đúng đường tra kho + mở picker khi
 * thêm quà CTKM (BXGY) từ `PromotionGiftBanner`, thay vì chép tay một bản
 * `useState`/`useRef`/`Promise` thứ hai dễ lệch hành vi (double-click, race
 * giữa hai lần gọi) với bản gốc.
 *
 * Caller tự render `<GiftVariantPickerDialog>` bằng `request` trả về —
 * hook này chỉ giữ state, không tự render JSX, để không ép caller theo một
 * bố cục modal cụ thể nào.
 */
export function useGiftVariantPicker() {
  const [request, setRequest] = useState<GiftVariantPickerRequest | null>(null);
  const resolveRef = useRef<((choice: PosGiftVariantOption | null) => void) | null>(null);

  function pickGiftVariant(
    productName: string,
    requiredQty: number,
    options: PosGiftVariantOption[],
  ): Promise<PosGiftVariantOption | null> {
    return new Promise((resolve) => {
      resolveRef.current = resolve;
      setRequest({ productName, requiredQty, options });
    });
  }

  function resolvePick(choice: PosGiftVariantOption | null) {
    setRequest(null);
    const resolve = resolveRef.current;
    resolveRef.current = null;
    resolve?.(choice);
  }

  return { request, pickGiftVariant, resolvePick };
}
